import { SolText, SolValidationBuilder, SolValidator, TableTools } from '@sol/components';
import { AsmTableColumnWidths } from '@asm/components-core/models/asm-setting';

/**
 * The width map a table layout stores, and the text an administrator edits it as: one `column: width`
 * per line. Text that does not read as widths is kept as typed, so the editor flags it rather than
 * dropping what was entered.
 */
export class AsmTableColumnWidthsTools {
  private constructor() {}

  private static readonly LINE = /^(\S+?)\s*:\s*(\d+)$/;

  public static readonly validator: SolValidator<AsmTableColumnWidths | string> = {
    validate: (builder: SolValidationBuilder, value: AsmTableColumnWidths | string | undefined, path: string) => {
      const isEmpty = value === undefined || (typeof value === 'string' && value.trim() === '');
      if (isEmpty || AsmTableColumnWidthsTools.toWidths(value) !== undefined) {
        return;
      }
      builder.add({
        path,
        type: 'error',
        text: SolText.of('asm-settings.admin.column-widths.invalid', {
          min: TableTools.MIN_COLUMN_WIDTH,
          max: TableTools.MAX_COLUMN_WIDTH,
        }),
      });
    },
  };

  /** Narrows a stored value to a width map; any width a table cannot take makes it no map. */
  public static toWidths(value: unknown): AsmTableColumnWidths | undefined {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return undefined;
    }
    const entries = Object.entries(value);
    if (!entries.every(([, width]) => TableTools.isColumnWidth(width))) {
      return undefined;
    }
    return Object.fromEntries(entries);
  }

  public static toLines(value: AsmTableColumnWidths | string): string[] {
    if (typeof value === 'string') {
      return [value];
    }
    return Object.entries(value).map(([id, width]) => `${id}: ${width}`);
  }

  public static toText(value: AsmTableColumnWidths | string | undefined): string | undefined {
    return value === undefined ? undefined : AsmTableColumnWidthsTools.toLines(value).join('\n');
  }

  /** Blank text sets no widths; text naming a column twice, or a width a table cannot take, comes back as typed. */
  public static fromText(text: string | undefined): AsmTableColumnWidths | string | undefined {
    const lines = (text ?? '')
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0);
    if (lines.length === 0) {
      return undefined;
    }
    const entries: [string, number][] = [];
    const ids = new Set<string>();
    for (const line of lines) {
      const match = AsmTableColumnWidthsTools.LINE.exec(line);
      const width = Number(match?.[2]);
      if (match === null || ids.has(match[1]) || !TableTools.isColumnWidth(width)) {
        return text;
      }
      ids.add(match[1]);
      entries.push([match[1], width]);
    }
    return Object.fromEntries(entries);
  }
}
